'use client';

import { useSyncExternalStore, type TransitionStartFunction } from 'react';

/**
 * **The Work list's unsaved entries** (docs/LOADING.md §2 tool 7, §3.4): what
 * the compose bar created and the list draws before the reply. The compose bar
 * and the list are separate components (the bar sits in the content head or in
 * the phone's dock, the list in the lens surface), so the entries live in this
 * one module-level store, the shape `SlotStore` and the tap mount's store use.
 *
 * One entry per submit. Its `key` is the create's idempotency key AND the
 * unsaved row's temporary id; `send` resends the create under that key, so
 * the row's "Not saved. Retry" can never make a second item. Whether a list
 * draws the entry is decided once, at the submit, by the list registered as
 * the HOST (`registerCreateHost`): the lens surface on its list rail, whose
 * criteria say whether the new item belongs there (`criteriaMatch`). An entry
 * the host does not take is spoken by the compose bar instead, in one line
 * under its field (question 6 of §10).
 *
 * An entry belongs to the surface it was submitted on (`host`, the lens's
 * surface key) and only that surface draws it. It leaves when the row it
 * stood for arrives from the server (`forgetCreates`, called by the host),
 * when the next submit replaces an entry no list draws, or when its host
 * leaves the screen after the reply settled it. An entry still in flight or
 * failed outlives its host; when that host mounts again it re-asks whether
 * the entry belongs, and drops one the reply settled whose row it does not
 * hold (the server's list is the truth there, whatever `criteriaMatch` said).
 */

export interface PendingCreate {
  /** The idempotency key, and the unsaved row's id until the reply names one. */
  key: string;
  /** The title as typed (`#word` tokens included; the row strips them). */
  typed: string;
  /** The item's id, once the reply named it. */
  serverId: string | null;
  /** Set when the create was refused or could not reach the server. */
  error: string | null;
  /** The surface it was submitted on, when a list was on screen. */
  host: string | null;
  /** That list draws this entry as a row. */
  drawn: boolean;
  /** A list was on screen and its criteria do not take the new item. */
  unmatched: boolean;
  /** Resend the create under the same key, inside the caller's transition. */
  send: (start: TransitionStartFunction) => void;
}

/** The list that draws entries: its surface key, and whether a typed title
 *  belongs to it. */
export interface CreateHost {
  id: string;
  accepts: (typed: string) => boolean;
}

let host: CreateHost | null = null;
let entries: readonly PendingCreate[] = [];
const listeners = new Set<() => void>();

function publish(next: readonly PendingCreate[]): void {
  entries = next;
  for (const listener of listeners) listener();
}

function patch(key: string, change: Partial<PendingCreate>): void {
  if (!entries.some((e) => e.key === key)) return;
  publish(entries.map((e) => (e.key === key ? { ...e, ...change } : e)));
}

/**
 * Register the list that draws entries. Its own leftovers are re-classified:
 * one the reply settled is dropped (the list's rows either hold its row, which
 * the list then draws as its own, or never will), and one still pending is asked `accepts` again (an
 * entry it no longer takes is no longer drawn; the compose bar's line speaks
 * of it only while that bar's latest create is still pending).
 *
 * Returns the unregister, which drops the entries that list drew and the reply
 * already settled. An entry still in flight, or failed and waiting for its
 * retry, stays for the list's next mount (a remount under a refresh, or the
 * person coming back), since it is the only record that the create may not
 * have landed.
 */
export function registerCreateHost(next: CreateHost): () => void {
  host = next;
  const kept: PendingCreate[] = [];
  let changed = false;
  for (const e of entries) {
    if (e.host !== next.id || !e.drawn) {
      kept.push(e);
      continue;
    }
    changed = true;
    if (e.serverId !== null) continue;
    const accepts = next.accepts(e.typed);
    kept.push({ ...e, drawn: accepts, unmatched: !accepts });
  }
  if (changed) publish(kept);
  return () => {
    if (host !== next) return;
    host = null;
    publish(entries.filter((e) => !e.drawn || e.serverId === null));
  };
}

/** Add a submit's entry. An earlier entry no list draws leaves with it: the
 *  compose bar's line speaks of the latest create only. */
export function addCreate(entry: Pick<PendingCreate, 'key' | 'typed' | 'send'>): PendingCreate {
  const accepts = host?.accepts(entry.typed);
  const created: PendingCreate = {
    ...entry,
    serverId: null,
    error: null,
    host: host?.id ?? null,
    drawn: accepts === true,
    unmatched: accepts === false,
  };
  publish([...entries.filter((e) => e.drawn), created]);
  return created;
}

/** The reply named the item. */
export function settleCreate(key: string, id: string): void {
  patch(key, { serverId: id, error: null });
}

/** The create was refused, or could not reach the server. */
export function failCreate(key: string, message: string): void {
  patch(key, { error: message });
}

/** A retry is under way: the entry reads as in flight again. */
export function resendCreate(key: string): void {
  patch(key, { error: null });
}

/** Drop the entries whose row the list now holds. */
export function forgetCreates(arrived: ReadonlySet<string>): void {
  const next = entries.filter((e) => !(e.serverId && arrived.has(e.serverId)));
  if (next.length !== entries.length) publish(next);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
const snapshot = () => entries;

/** Every entry as it stands (outside React; the hook below re-renders). */
export function pendingCreates(): readonly PendingCreate[] {
  return entries;
}

/** Every entry, re-rendering when any changes. */
export function usePendingCreates(): readonly PendingCreate[] {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
